import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {PearlHarborGameAssets} from "../PearlHarborGameAssets";
import {PearlHarbor} from "../PearlHarbor";
import {IWinRatioConfig, PearlHarborWinConfigs} from "../configs/PearlHarborWinConfigs";
import {gsap} from 'gsap';
import {PearlHarborGameConfig} from "../PearlHarborGameConfig";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {PearlHarborHelper} from "../utils/PearlHarborHelper";
import {PearlHarborSoundManager} from "../sound/PearlHarborSoundManager";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";

export class PearlHarborTotalWinView extends PIXI.Container implements StageLayer {

    private _totalWinCountUp: BasicBitmapCountUp;
    private _bombAnimationUnderCountUp: PIXI.spine.Spine;
    private _totalWinContainer: PIXI.Container;

    private _fadeInTimeline: gsap.core.Timeline;
    private _fadeOutTimeline: gsap.core.Timeline;
    private _countUpTimeline: gsap.core.Timeline;
    private _totalWinAmount: number;
    private _winRatioConfig: IWinRatioConfig;
    private _completeCallback: Function;
    private _targetScale: number;
    private static FADE_IN_TIME: number = 0.3;

    constructor() {
        super();
        this.name = "TOTAL_WIN";
        this.init();
    }

    private init(): void {
        this._totalWinContainer = new PIXI.Container();
        this._totalWinContainer.position.set(360);
        this._bombAnimationUnderCountUp = new PIXI.spine.Spine(PearlHarbor.assetLoader.getSpineAsset("total_win"));
        this.addChild(this._bombAnimationUnderCountUp);
        this.addChild(this._totalWinContainer);
        this._bombAnimationUnderCountUp.visible = false;
        this._totalWinCountUp = new BasicBitmapCountUp({
            fontName: PearlHarborGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 300,
            tint: 0xffffff
        }, 680, new PIXI.Point(0.5, 0.5));
        this._totalWinCountUp.visible = false;
        this._totalWinCountUp.position.y = -50;
        this._totalWinContainer.addChild(this._totalWinCountUp);

        this.onResize();
    }

    private fadeOutWinPres(duration: number, delay: number): void {
        this._fadeOutTimeline = gsap.timeline({
            delay: delay
        });
        this._fadeOutTimeline.add([
            gsap.to(this._totalWinCountUp, {
                duration: duration,
                alpha: 0,
                ease: "power3.out"
            })
        ]);

        this._fadeOutTimeline.add(() => {
            this._totalWinCountUp.visible = false;
        });
    }

    private setTotalWinForAbort(): void {
        PearlHarborSoundManager.playCountUpEnds();
        this._totalWinCountUp.alpha = 1;
        this._totalWinCountUp.scale.set(this._targetScale);
        this._totalWinCountUp.visible = true;
        this._totalWinCountUp.stopAt(this._totalWinAmount);
    }

    private callCompleteFunction() {
        if (this._completeCallback) {
            this._completeCallback();
        }
    }

    public getWinRatioConfig(): IWinRatioConfig {
        return PearlHarborWinConfigs.getWinConfig();
    }

    public onResize(): void {
        this.position = PearlHarborGameConfig.TOTAL_WIN_VIEW_POSITION;
        if (!NolimitApplication.isLandscape) {
            this.position.y = this.position.y - (PearlHarborGameConfig.PORTRAIT_Y_OFFSET + NolimitApplication.screenBounds.top) / 2;
        }
    }

    public getTotalWinTimeline(totalWin: number, waysWon: number, completeCallback: Function): gsap.core.Timeline {
        if (this._fadeOutTimeline && this._fadeOutTimeline.isActive()) {
            this._fadeOutTimeline.pause();
            this._fadeOutTimeline.kill();
        }

        this._totalWinAmount = totalWin;
        this._winRatioConfig = this.getWinRatioConfig();
        this._completeCallback = completeCallback;

        this._totalWinCountUp.scale.set(0.5, 0.5);
        let targetScale = 0.5;
        let winAnimationName = "normal";
        if (this._winRatioConfig.type == PearlHarborWinConfigs.SMALL) {
            targetScale = 0.6;
            winAnimationName = "small";
        } else if (this._winRatioConfig.type == PearlHarborWinConfigs.MEDIUM) {
            winAnimationName = "medium";
            targetScale = 0.7;
        } else if (this._winRatioConfig.type == PearlHarborWinConfigs.LARGE) {
            targetScale = 0.8;
            winAnimationName = "large";
        }
        this._targetScale = targetScale;

        this._countUpTimeline = gsap.timeline();
        if (this._winRatioConfig.duration == 0 || PearlHarborHelper.isWinBelowStake()) {
            this._totalWinCountUp.getCountAnimation(this._totalWinAmount, this._totalWinAmount, this._winRatioConfig.duration);
            this._totalWinCountUp.scale.set(targetScale, targetScale);
            PearlHarbor.keypadPlugin.setWin(PearlHarbor.model.gameData.accumulatedRoundWin, true,PearlHarborHelper.isWinBelowStake());
        } else {
            this._countUpTimeline.add(this._totalWinCountUp.getCountAnimation(0, this._totalWinAmount, this._winRatioConfig.duration));
            this._countUpTimeline.add(()=>{this.getBombAnimationUnderCountUp(winAnimationName)});
            this._countUpTimeline.add(gsap.to(this._totalWinCountUp.scale, {
                duration: this._winRatioConfig.duration,
                x: targetScale,
                y: targetScale,
                ease: "power3.out"
            }), "<");
            this._countUpTimeline.add(() => {
                this._totalWinCountUp.stopAt(this._totalWinAmount);
            });
            this._countUpTimeline.add(() => PearlHarbor.keypadPlugin.setWin(PearlHarbor.model.gameData.accumulatedRoundWin, true, PearlHarborHelper.isWinBelowStake()));
        }

        this._totalWinCountUp.alpha = 0;
        this._totalWinCountUp.visible = !PearlHarborHelper.isWinBelowStake();

        this._fadeInTimeline = gsap.timeline();
        this._fadeInTimeline.add([
            gsap.to(this._totalWinCountUp, {
                duration: PearlHarborTotalWinView.FADE_IN_TIME,
                alpha: 1,
                ease: "power3.out"
            })
        ]);

        if (this._countUpTimeline.totalDuration() === 0) {
            this._fadeInTimeline.add(() => this.fadeOutWinPres(0.5, 0), "+=0.5");
            this._countUpTimeline.add(() => this.callCompleteFunction());
            return this._fadeInTimeline;
        } else {
            this._countUpTimeline.add(() => this.fadeOutWinPres(0.5, 0), "+=0.8");
            this._countUpTimeline.add(() => this.callCompleteFunction());
            return this._countUpTimeline;
        }
    }

    public getBombAnimationUnderCountUp(animationName:string):SpineTween{
        if(animationName === "medium" || animationName === "large"){
            //medium, large are used
            const spineTween: SpineTween = new SpineTween(this._bombAnimationUnderCountUp, 0, animationName);
            this._bombAnimationUnderCountUp.visible = true;
            this._bombAnimationUnderCountUp.position.set(360,360);
            return spineTween;
        }

        return new gsap.core.Timeline();
    }

    // If aborted by new spin
    public abort(): void {
        if (this._countUpTimeline && this._countUpTimeline.isActive()) {
            this._countUpTimeline.pause();
            this._countUpTimeline.kill();

            PearlHarbor.keypadPlugin.setWin(PearlHarbor.model.gameData.accumulatedRoundWin, true, PearlHarborHelper.isWinBelowStake());

            this.setTotalWinForAbort();
            this.fadeOutWinPres(0.3, 0);
        }
    }

    // If aborted by full screen clicking
    public hardAbort(): void {
        if (this._fadeInTimeline && this._fadeInTimeline.isActive()) {
            this._fadeInTimeline.pause();
            this._fadeInTimeline.kill();

            this.setTotalWinForAbort();
            this.fadeOutWinPres(0.3, 0);
        }

        if (this._countUpTimeline && this._countUpTimeline.isActive()) {
            this._countUpTimeline.pause();
            this._countUpTimeline.kill();

            PearlHarbor.keypadPlugin.setWin(PearlHarbor.model.gameData.accumulatedRoundWin, true, PearlHarborHelper.isWinBelowStake());

            this.setTotalWinForAbort();
            this.fadeOutWinPres(0.5, 0.5);
        }

        this.callCompleteFunction();
    }
}
